/**
 * Module Status Grid Component
 *
 * Visual grid showing health status of all platform modules.
 * Each module shows a status badge (healthy/warning/critical/unavailable)
 * and is clickable to navigate to the module's dashboard.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiMinusCircle,
  FiCpu,
  FiCloud,
  FiDatabase,
  FiZap,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';

type StatusKey = 'healthy' | 'warning' | 'critical' | 'unavailable';

interface StatusConfig {
  icon: IconType;
  color: string;
  bg: string;
  border: string;
  label: string;
}

const statusConfig: Record<StatusKey, StatusConfig> = {
  healthy: {
    icon: FiCheckCircle,
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    label: 'Healthy',
  },
  warning: {
    icon: FiAlertTriangle,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    label: 'Warning',
  },
  critical: {
    icon: FiXCircle,
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    label: 'Critical',
  },
  unavailable: {
    icon: FiMinusCircle,
    color: 'text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-900/20',
    border: 'border-gray-200 dark:border-gray-700',
    label: 'Unavailable',
  },
};

const moduleIcons: Record<string, IconType> = {
  GenAIOps: FiZap,
  AgentOps: FiCpu,
  DataOps: FiDatabase,
  CloudOps: FiCloud,
};

interface Module {
  name: string;
  status: string;
  link?: string;
  metric?: string;
}

interface ModuleStatusData {
  modules?: Module[];
}

interface Props {
  data?: ModuleStatusData;
}

const ModuleStatusGrid: React.FC<Props> = ({ data }) => {
  const navigate = useNavigate();
  const modules = data?.modules || [];

  if (modules.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        No module data available
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 h-full p-2">
      {modules.map((module) => {
        const config = statusConfig[module.status as StatusKey] || statusConfig.unavailable;
        const StatusIcon = config.icon;
        const ModuleIcon = moduleIcons[module.name] || FiCpu;

        return (
          <button
            key={module.name}
            onClick={() => module.link && navigate(module.link)}
            className={`
              ${config.bg} ${config.border}
              border rounded-lg p-4
              flex flex-col items-center justify-center
              transition-all duration-200
              hover:shadow-md hover:scale-[1.02]
              cursor-pointer
              focus:outline-none focus:ring-2 focus:ring-blue-500
            `}
          >
            <div className="flex items-center gap-2 mb-2">
              <ModuleIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {module.name}
              </span>
            </div>

            <div className="flex items-center gap-1.5 mb-1">
              <StatusIcon className={`w-4 h-4 ${config.color}`} />
              <span className={`text-xs font-medium ${config.color}`}>
                {config.label}
              </span>
            </div>

            {module.metric && module.metric !== 'N/A' && (
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {module.metric}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ModuleStatusGrid;
